import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { NameDto } from '@/api/Common/Types'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { fetchTickmillCompaniesAction } from '@/store/identity/identity.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { UpdateReferralMaterialDto } from 'api/ClientArea/ReferralMaterialsDto'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchReferralMaterialsCategoriesByEntity,
  fetchReferralMaterialsPromoContentTypes,
  fetchReferralMaterialsPromoSizes,
  fetchReferralMaterialsTypes,
} from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { ReferralMaterialsDetailsForm } from './referralMaterialsDetailsForm'
import { RelatedPromoMaterialTable } from './RelatedPromoMaterialTable'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  referralMaterialsTypes: useAppSelector((state) => state.clientArea.referralMaterialsTypes),
  referralMaterialsPromoSizes: useAppSelector(
    (state) => state.clientArea.referralMaterialsPromoSizes
  ),
  referralMaterialsPromoContentTypes: useAppSelector(
    (state) => state.clientArea.referralMaterialsPromoContentTypes
  ),
  referralMaterialsCategoriesByEntity: useAppSelector(
    (state) => state.clientArea.referralMaterialsCategoriesByEntity
  ),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const fetchReferralMaterialsCategoriesByEntityCallback = useCallback(
    async (tickmillCompanyId: number | string, isCategoryActive?: boolean) =>
      dispatch(
        fetchReferralMaterialsCategoriesByEntity(
          clientAreaApiClient,
          tickmillCompanyId,
          isCategoryActive
        )
      ),
    [clientAreaApiClient, dispatch]
  )

  return {
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchReferralMaterialsTypes: async () =>
      dispatch(fetchReferralMaterialsTypes(clientAreaApiClient)),
    fetchReferralMaterialsPromoSizes: async () =>
      dispatch(fetchReferralMaterialsPromoSizes(clientAreaApiClient)),
    fetchReferralMaterialsPromoContentTypes: async () =>
      dispatch(fetchReferralMaterialsPromoContentTypes(clientAreaApiClient)),
    fetchReferralMaterialsCategoriesByEntity: fetchReferralMaterialsCategoriesByEntityCallback,
  }
}

const useReferralMaterialsFetch = () => {
  const {
    languages,
    tickmillCompanies,
    referralMaterialsTypes,
    referralMaterialsPromoSizes,
    referralMaterialsPromoContentTypes,
    referralMaterialsCategoriesByEntity,
  } = useMapStateToProps()
  const {
    fetchClientLanguages,
    fetchReferralMaterialsTypes,
    fetchReferralMaterialsPromoSizes,
    fetchReferralMaterialsPromoContentTypes,
    fetchReferralMaterialsCategoriesByEntity,
    fetchTickmillCompanies,
  } = useMapDispatchToProps()

  const { referralMaterialsData, forceRefresh, isLoading } = usePromoMaterialsLandingsFetch()

  useEffect(() => {
    if (isEmpty(languages)) {
      fetchClientLanguages()
    }
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
    if (isEmpty(referralMaterialsTypes)) {
      fetchReferralMaterialsTypes()
    }
    if (isEmpty(referralMaterialsPromoSizes)) {
      fetchReferralMaterialsPromoSizes()
    }
    if (isEmpty(referralMaterialsPromoContentTypes)) {
      fetchReferralMaterialsPromoContentTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    referralMaterialsData,
    forceRefresh,
    isLoading,
    languages,
    tickmillCompanies,
    referralMaterialsTypes,
    referralMaterialsPromoSizes,
    referralMaterialsPromoContentTypes,
    referralMaterialsCategoriesByEntity,
    fetchReferralMaterialsCategoriesByEntity,
  }
}

const usePromoMaterialsLandingsFetch = () => {
  const { referralMaterialsId } = useReferralMaterialsRoute()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!referralMaterialsId) {
      return
    }

    return await clientAreaApiClient.getReferralMaterialsById(referralMaterialsId)
  }, [referralMaterialsId]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: referralMaterialsData, isLoading } = useFetchOne(callback)

  return { referralMaterialsData, isLoading, forceRefresh }
}

const useReferralMaterialsMutate = () => {
  const { referralMaterialsId } = useReferralMaterialsRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleEditSubmit = async (data: UpdateReferralMaterialDto) => {
    if (referralMaterialsId) {
      await clientAreaApiClient.updateReferralMaterial(referralMaterialsId, data)
      setToast(successToast(`IB Referral Material Page updated successfully.`))
    }
  }

  return { handleEditSubmit }
}

const useReferralMaterialsRoute = () => {
  const params = useParams<{ id: string }>()
  const referralMaterialsId = params.id

  return { referralMaterialsId }
}

const ReferralMaterialsDetailsPage = () => {
  const crumbs = [{ title: 'Marketing - Referral Materials', link: links.referralMaterials.url }]
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const referralMaterialsFetch = useReferralMaterialsFetch()
  const {
    referralMaterialsData,
    isLoading,
    forceRefresh,
    languages,
    tickmillCompanies,
    referralMaterialsTypes,
    referralMaterialsCategoriesByEntity,
    fetchReferralMaterialsCategoriesByEntity,
  } = referralMaterialsFetch

  const mutate = useReferralMaterialsMutate()

  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const [allowedSizes, setAllowedSizes] = useState<NameDto[]>([])

  const handleSubmit = async (updateReferralMaterial: UpdateReferralMaterialDto) => {
    await mutate.handleEditSubmit(updateReferralMaterial)
    forceRefresh()
  }

  const fetchReferralMaterialsAllowedSizes = async (referralMaterialTypeId: number) => {
    const allowedSizes =
      await clientAreaApiClient.getReferralMaterialsAllowedSizes(referralMaterialTypeId)
    setAllowedSizes(allowedSizes)
  }

  useEffect(() => {
    if (referralMaterialsData?.type) {
      fetchReferralMaterialsAllowedSizes(referralMaterialsData.type.id)
    }
  }, [referralMaterialsData?.type])

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          referralMaterialId={referralMaterialsData?.id}
        />
      )}
      {referralMaterialsData?.name && (
        <Breadcrumbs crumbs={crumbs.concat({ title: referralMaterialsData.name, link: '#' })} />
      )}
      {referralMaterialsData && <NavigationHeader />}
      <Loading isLoading={isLoading}>
        {referralMaterialsData && (
          <ReferralMaterialsDetailsForm
            data={referralMaterialsData}
            languages={languages.filter((lang) => lang.clientAreaVisible)}
            tickmillCompanies={tickmillCompanies}
            referralMaterialsTypes={referralMaterialsTypes}
            referralMaterialsCategoriesByEntity={referralMaterialsCategoriesByEntity}
            fetchReferralMaterialsCategoriesByEntity={fetchReferralMaterialsCategoriesByEntity}
            setAuditLogModal={setAuditLogVisible}
            onSubmit={handleSubmit}
          />
        )}
      </Loading>
      {referralMaterialsData && (
        <RelatedPromoMaterialTable
          caLanguages={referralMaterialsData.caLanguages}
          sizes={allowedSizes}
          tickmillCompanyId={referralMaterialsData.tickmillCompany?.id}
          updateReferralMaterial={() => forceRefresh()}
        />
      )}
    </React.Fragment>
  )
}

export default ReferralMaterialsDetailsPage
